import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Grid,
  Card,
  Paper,
  Typography,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import CallMade from '@mui/icons-material/CallMade';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ReactApexChart from 'react-apexcharts';
import Iconify from 'src/components/iconify';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import { toast } from 'react-toastify';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const ARAC_RENKLERI = { "Küçük Araç": "#1C64F2", "Büyük Araç": "#16BDCA", "Palet": "#FDBA8C", "Koli / Kargo": "#E74694", "Ring": "#6366F1", "Askılı Araç": "#FACC15" };

export default function MallManagementGoodsAcceptance() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  const [allMalKabulList, setAllMalKabulList] = useState([]);
  const [filteredMalKabulList, setFilteredMalKabulList] = useState([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 50;

  const [filterText, setFilterText] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  const [selectedStatusTitle, setSelectedStatusTitle] = useState('Tümü');

  // Check service permission using hook
  const { hasPermission: goodsAcceptancePermission, loading: permissionLoading } = useServicePermission(SERVICES.GOODS_ACCEPTANCE);

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Mal Kabul',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  const statusCounts = useMemo(() => {
    const counts = new Map();
    // "Tümü" seçeneğini toplam kayıt sayısı ile başlat
    counts.set('Tümü', allMalKabulList.length);

    // Her bir öğeyi dolaşarak statusTitle'a göre say
    allMalKabulList.forEach((item) => {
      const title = item.statusTitle || 'Bilinmiyor'; // statusTitle yoksa 'Bilinmiyor' de
      counts.set(title, (counts.get(title) || 0) + 1);
    });

    // Map'i sıralı bir diziye dönüştür (Önce Tümü, sonra diğerleri)
    const sortedCounts = new Map();
    sortedCounts.set('Tümü', counts.get('Tümü'));
    counts.delete('Tümü');

    // Kalanları sayısına göre çoktan aza sırala
    const sortedEntries = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    sortedEntries.forEach(([key, value]) => {
      sortedCounts.set(key, value);
    });

    return sortedCounts;
  }, [allMalKabulList]);

  useEffect(() => {
    if (permissionLoading) {
      return;
    }

    const getLiveAnalytics = async () => {
      if (!mallID) { setLoading(false); return; }
      setLoading(true);
      try {
        const userHasPermission = goodsAcceptancePermission;
        setHasPermission(userHasPermission);
        if (userHasPermission) {
          const response = await Services.GetLiveAnalyticsMallMalKabul(parsedUser.token, mallID);
          if (response.data && response.data.result) {
            setAnalyticsData(response.data.result);
          } else {
            setAnalyticsData({});
          }
        } else {
          setAnalyticsData({});
          setShowApplyBanner(true);
        }
      } catch (error) {
        console.error("Veri yüklenirken hata oluştu:", error);
        setAnalyticsData({});
      } finally {
        setLoading(false);
      }
    };
    getLiveAnalytics();
  }, [mallID, parsedUser?.token, goodsAcceptancePermission, permissionLoading]);

  useEffect(() => {
    const list = analyticsData?.malkabulList || [];
    setAllMalKabulList(list);
    setFilteredMalKabulList(list);
  }, [analyticsData]);

  const getFilteredListFromAPI = () => {
    if (!hasPermission) return;
    const startDateFormatted = startDate ? startDate.format('YYYY-MM-DD') : '';
    const endDateFormatted = endDate ? endDate.format('YYYY-MM-DD') : '';
    Services.GetLiveAnalyticsMallMalKabulList(parsedUser.token, mallID, startDateFormatted, endDateFormatted)
      .then((res) => {
        const listFromApi = res.data.result || [];
        setAllMalKabulList(listFromApi);
        setFilteredMalKabulList(listFromApi);
        setPage(1);
      }).catch((err) => toast.error("Liste güncellenirken bir hata oluştu."));
  };

  useEffect(() => {
    let filtered = allMalKabulList;
    const aramaMetni = filterText.trim().toLowerCase();
    
    if (selectedStatusTitle !== 'Tümü') {
      filtered = filtered.filter((item) => (item.statusTitle || 'Bilinmiyor') === selectedStatusTitle);
    }
    if (aramaMetni !== '') {
      filtered = filtered.filter((item) => {
        const fromLocation = (item.fromLocationTitle || '').toLowerCase();
        const toLocation = (item.toLocationTitle || '').toLowerCase();

        return fromLocation.includes(aramaMetni) || toLocation.includes(aramaMetni);
      });
    }
    
    setFilteredMalKabulList(filtered);
    setPage(1);
  }, [filterText, allMalKabulList, selectedStatusTitle]);

  const handleClearFilters = () => {
    setFilterText('');
    setStartDate(null);
    setEndDate(null);
    setFilteredMalKabulList(analyticsData?.malkabulList || []);
    setSelectedStatusTitle('Tümü'); // <-- STATÜ FİLTRESİNİ SIFIRLA
    setAllMalKabulList(analyticsData?.malkabulList || []);
    setPage(1);
  };

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') { return series.map(() => 0); }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
    }
    return [];
  };

  const charts = useMemo(() => {
    const data = analyticsData || {};
    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const currentMonthAbbrev = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(now);
    const commonBarPlot = { bar: { columnWidth: '20%', borderRadius: 8, borderRadiusApplication: 'end' } };
    const weeklyBarPlot = { bar: { columnWidth: '40%', borderRadius: 8, borderRadiusApplication: 'end' } };

    // Haftalık Mal Kabul - Muhaberat Hareketleri (MallManagementStatistics'den taşındı)
    const muhaberatMonthly = data.monthlyMalKabulMuhaberatHandshakes || {};
    const muhaberatTimeRanges = Object.keys(muhaberatMonthly);
    const vehicleTypes = [...new Set(muhaberatTimeRanges.flatMap(range => Object.keys(muhaberatMonthly[range] || {})))];
    const vehicleColors = vehicleTypes.reduce((map, type) => ({ ...map, [type]: ARAC_RENKLERI[type] || '#ccc' }), {});
    const monthlyChart = {
      series: vehicleTypes.map(type => ({ name: type, data: muhaberatTimeRanges.map(range => muhaberatMonthly[range]?.[type] ?? 0) })),
      options: {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        plotOptions: commonBarPlot,
        colors: vehicleTypes.map(type => vehicleColors[type]),
        legend: { position: 'bottom' },
        xaxis: { categories: muhaberatTimeRanges.map(range => `${currentMonthAbbrev} ${range.replace('-rest', `-${lastDayOfMonth}`)}`) },
        dataLabels: { enabled: false },
        yaxis: { title: { text: 'Taşıma Adedi' } }
      },
    };

    const tools = data.monthlyTransportationTools || {};
    const toolKeys = Object.keys(tools);
    const toolCounts = toolKeys.map(k => parseFloat(tools[k]) || 0);
    const toolTotal = toolCounts.reduce((sum, v) => sum + v, 0) || 1;
    const vehicleChart = {
      series: toolCounts,
      options: {
        chart: { type: 'donut', toolbar: { show: false } },
        labels: toolKeys,
        colors: toolKeys.map(key => ARAC_RENKLERI[key] || '#94A3B8'),
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600,
                  offsetY: -5
                },
                value: {
                  show: true,
                  fontSize: '28px',
                  fontWeight: 700,
                  formatter: (val) => `% ${((val / toolTotal) * 100).toFixed(0)}`
                },
                total: { show: false }
              }
            }
          }
        },
        legend: { position: 'bottom' },
        tooltip: { y: { formatter: (val) => `${val} adet (${((val / toolTotal) * 100).toFixed(1)}%)` } },
        dataLabels: { enabled: false }
      },
    };

    const weeklyDirectionsData = data.weeklyDirections || {};
    const startOfTargetWeek = moment().day(0); 

    const weeklyLabels = [];
    const seriesInbound = [];
    const seriesWarehouse = [];
    const seriesOutbound = [];

    for (let i = 0; i < 7; i++) {
      const currentDate = startOfTargetWeek.clone().add(i, 'days');
      const currentDateStr = currentDate.format('YYYY-MM-DD');
      const dayLabel = currentDate.format('DD MMMM');

      const dayData = weeklyDirectionsData[currentDateStr] || {};
      
      // Veri yoksa bile 0 basarak grafiğin iskeletini koruyoruz
      const inbound = dayData.StoreToMallLogistics || 0;
      const warehouse = dayData.StoreToStore || 0;
      const outbound = dayData.MallLogisticsToStore || 0;

      weeklyLabels.push(dayLabel);
      seriesInbound.push(inbound);
      seriesWarehouse.push(warehouse);
      seriesOutbound.push(outbound);
    }

    const weeklyDirectionsChart = {
      series: [
        { name: 'Dışarı Çıkan Paket', data: seriesInbound },
        { name: 'Mağaza - Depo Arası', data: seriesWarehouse },
        { name: 'İçeri Giren Paket', data: seriesOutbound }
      ],
      options: {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        plotOptions: weeklyBarPlot,
        stroke: { show: true, width: 1, colors: ['#fff'] },
        colors: ['#1C64F2', '#FDBA8C', '#16BDCA'],
        xaxis: { 
          categories: weeklyLabels,
          labels: { style: { fontSize: '11px' } } 
        },
        yaxis: {
          title: { text: 'Toplam Paket Adedi' },
          labels: { formatter: (val) => val.toFixed(0) }
        },
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (val) => `${val} adet` } }
      },
    };

    const medianData = data.allTimesTransportationTimeMedianByDays || {};
    const dayOrder = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
    const dayAbbrev = ['P.tesi', 'Salı', 'Çarş', 'Perş', 'Cuma', 'C.tesi', 'Pazar'];
    const avgChart = {
      series: [{ name: 'Süre (dk)', data: dayOrder.map((d) => Math.floor(medianData[d] ?? 0)) }],
      options: {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        plotOptions: {
          bar: {
            columnWidth: '50%',
            borderRadius: 8
          }
        },
        colors: ['#1C64F2'],
        xaxis: {
          categories: dayAbbrev
        },

        yaxis: {
          title: { text: 'dk' },
          labels: {
            formatter: (val) => val.toFixed(0),
          },
        },
        dataLabels: { enabled: false }
      },
    };

    return { monthlyChart, vehicleChart, weeklyDirectionsChart, avgChart, postStatuses: data.postStatuses };
  }, [analyticsData]);

  const stats = [
    { title: 'Açıkta Bekleyen İşlemler', value: charts.postStatuses?.waiting ?? 0, gradient: 'linear-gradient(135deg,#F7A937 0%,#DC9328 100%)' },
    // { title: 'Porter Üzerinde İşlemler', value: charts.postStatuses?.on_porter ?? 0, gradient: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)' },
    { title: 'Teslim Edilen İşlemler', value: charts.postStatuses?.completed ?? 0, gradient: 'linear-gradient(135deg,#3DBFA0 0%,#0E927A 100%)' },
  ];
  const maskName = (name) => !name || name.length === 0 ? '' : name.charAt(0) + '*'.repeat(name.length - 1);
  const calculateDuration = (start, end) => {
    const diff = parseInt(end) - parseInt(start);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
function exportGoodsAcceptanceToExcel(data, calculateDurationFn) {
    const exportData = data.map((item, index) => ({
      id: index + 1,
      createdDate: item.createdCateTime
        ? moment.unix(parseInt(item.createdCateTime, 10)).format('DD.MM.YYYY HH:mm:ss')
        : '-',
      
      porterStatus: item.porterStatus === '1' ? 'Aktif' : 'Pasif',
      documentNumber: item.createdBy ?? '-',
      fromLocation: item.fromLocationTitle ?? '-',
      toLocation: item.toLocationTitle ?? '-',
      transportationTool: item.details?.[0]?.transportationTool ?? '-',
      status: item.statusTitle ?? '-',
      duration: (item.postCreatedBy === 'corporational' && item.onPorterDateTime && item.createdCateTime)
        ? calculateDurationFn(item.createdCateTime, item.onPorterDateTime) 
        : '--:--:--'
    }));

    api.post('excel/export', {
      type: 'excel',
      content: exportData,
      header: {
        No: 'number',
        'Oluşturma Tarihi': 'string',
        'Durum': 'string',
        'Belge No': 'string',
        'Çıkış Lokasyonu': 'string',
        'Varış Lokasyonu': 'string',
        'Araç Tipi': 'string',
        'Statü': 'string',
        'İşlem Süresi': 'string'
      },
      sheetTitle: 'Mal Kabul İşlemleri'
    })
      .then(response => { if (response.data.status) { window.open(response.data.result, '_blank'); } else { toast.error(response.data.message); } }).catch(error => toast.error(error.message));
  }

  if (loading || permissionLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }

  const paginatedMalKabulList = filteredMalKabulList.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const startDateLabel = startDate ? startDate.format('DD.MM.YYYY') : '-';
  const endDateLabel = endDate ? endDate.format('DD.MM.YYYY') : '-';

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet><title>Mal Kabul İşlemleri</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Mal Kabul İşlemleri</Typography>
        {/* <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenVideoModal}>Tanıtım Videosunu İzle</Button> */}
      </Stack>

      <Grid container spacing={2}>
        {[
          { title: 'Haftalık Mal Kabul - Muhaberat Hareketleri', subtitle: 'Taşıma Adedi', chart: charts.monthlyChart, type: 'bar' },
          { title: 'Aylık Kullanılan Araç Dağılımı', subtitle: 'Taşıma Aracı Adetine Göre', chart: charts.vehicleChart, type: 'donut' },
          { title: 'Günlük Mal Kabul Hareketleri', subtitle: 'Toplam Adet', chart: charts.weeklyDirectionsChart, type: 'bar' },
          { title: 'İş Emri Zimmete Alma Süresi (Mağaza Talepleri)', subtitle: 'Ortalama Taşıma Süreleri', chart: charts.avgChart, type: 'bar' },
        ].map((cfg, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card sx={{ position: 'relative', height: 360, p: 2, bgcolor: 'background.paper' }} elevation={0}>
              <Typography variant="h6" gutterBottom>{cfg.title}</Typography>
              <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>{cfg.subtitle}</Typography>
              {cfg.chart ? <ReactApexChart options={cfg.chart.options} series={hasPermission ? cfg.chart.series : zeroOutSeries(cfg.chart.series)} type={cfg.type} height={260} /> : <Box display="flex" justifyContent="center" alignItems="center" height={260}><Typography>Veri yok</Typography></Box>}
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }} elevation={0}>
        <Grid container spacing={2}>
          {stats.map((s, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: 2, background: s.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>{i === 0 ? <CallMade sx={{ color: '#fff', fontSize: 24 }} /> : <Iconify icon={s.icon} width={24} height={24} sx={{ color: '#fff' }} />}</Box>
                <Typography variant="subtitle2" color="text.secondary">{s.title}</Typography>
                <Typography variant="h4" sx={{ mt: 1, fontWeight: 600 }}>{s.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField fullWidth sx={{ height: 56 }} placeholder="Mağaza Adına Göre Ara" value={filterText} onChange={(e) => setFilterText(e.target.value)} InputProps={{ startAdornment: 
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment> 
            }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker                 
                value={startDate} 
                onChange={setStartDate} 
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Başlangıç Tarihi" 
                    fullWidth 
                    size="small"
                  />
                )} 
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker                 
                value={endDate} 
                onChange={setEndDate} 
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Bitiş Tarihi" 
                    fullWidth 
                    size="small"                     
                  />
                )} 
              />
            </LocalizationProvider>
          </Grid>                   
          <Grid item xs={4} md={1}><Button onClick={getFilteredListFromAPI} fullWidth variant="contained" sx={{height: 56}} disabled={!hasPermission}>FİLTRELE</Button></Grid>        
          <Grid item xs={4} md={1}><Button onClick={handleClearFilters} fullWidth variant="outlined" color="secondary" sx={{height: 56}} disabled={!hasPermission}>TEMİZLE</Button></Grid>                    
          <Grid item xs={4} md={2}><Button onClick={() => exportGoodsAcceptanceToExcel(filteredMalKabulList, calculateDuration)} fullWidth variant="contained" color="info" sx={{height: 56}} disabled={!hasPermission}>EXCEL'E AKTAR</Button></Grid>
        </Grid>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt:2 }}>
          {[...statusCounts.entries()].map(([title, count]) => (
            <Chip
              key={title}
              label={`${title} (${count})`}
              clickable
              onClick={() => setSelectedStatusTitle(title)}
              color={selectedStatusTitle === title ? 'primary' : 'default'}
              variant={selectedStatusTitle === title ? 'filled' : 'outlined'}
              sx={{ mb: 1 }} // wrap için alt boşluk
            />
          ))}
        </Stack>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.paper' }}><TableRow>{['PORTER ADI', 'TARİH', 'DURUMU', 'BELGE NO', 'ÇIKIŞ NOKTASI', 'VARIŞ NOKTASI', 'ARAÇ TİPİ', 'STATÜ', 'İŞLEM SÜRESİ'].map((h) => (<TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>))}</TableRow></TableHead>
            <TableBody>
              {filteredMalKabulList.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Seçili olan tarihler({startDateLabel}-{endDateLabel}) arasında kayıt bulunamadı.
                </Alert>
              )}
              {paginatedMalKabulList.map((r, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>{r.porterName} {r.porterLastname}</TableCell>
                  <TableCell>
                    {r.createdCateTime 
                      ? moment.unix(parseInt(r.createdCateTime)).format('DD.MM.YYYY HH:mm') 
                      : '-'}
                  </TableCell>
                  <TableCell><Box component="span" sx={{ px: 1.5, py: 0.5, borderRadius: '20px', border: '1px solid', borderColor: r.porterStatus === '1' ? '#BBF7D0' : '#DFE2E4', bgcolor: r.porterStatus === '1' ? '#F0FDF4' : '#FAFAFA', color: r.porterStatus === '1' ? '#15803D' : '#4F575F', fontSize: 12, fontWeight: 600 }}>{r.porterStatus === '1' ? 'Aktif' : 'Pasif'}</Box></TableCell>
                  <TableCell>{r.createdBy}</TableCell><TableCell>{r.fromLocationTitle}</TableCell><TableCell>{r.toLocationTitle}</TableCell>
                  <TableCell>{r.details?.[0]?.transportationTool || '-'}</TableCell><TableCell>{r.statusTitle}</TableCell>
                  <TableCell>
                    {r.postCreatedBy === 'corporational' && r.onPorterDateTime && r.createdCateTime
                      ? calculateDuration(r.createdCateTime, r.onPorterDateTime)
                      : '--:--:--'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow><TableCell colSpan={8} sx={{ py: 2 }}><Grid container alignItems="center"><Grid item xs><Typography variant="caption">{`Toplam ${filteredMalKabulList.length} kayıt`}</Typography></Grid><Grid item><Pagination count={Math.ceil(filteredMalKabulList.length / rowsPerPage)} page={page} onChange={(e, val) => setPage(val)} shape="rounded" size="small" /></Grid></Grid></TableCell></TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Videosu<IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers><video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/inbound-sv.mp4">Tarayıcınız video etiketini desteklemiyor.</video></DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}